import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../firebase";
import Sidebar from "../../components/dashboard/Sidebar";
import { generateReceipt } from "../../utils/generateReceipt";

const STATUS_COLORS = {
  Success: { bg: "#D1FAE5", text: "#065F46" },
  Failed: { bg: "#FEE2E2", text: "#991B1B" },
  Initiated: { bg: "#FEF3C7", text: "#92400E" },
};

const PaymentHistoryScreen = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [history, setHistory] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) { navigate("/login"); return; }
      setUser(u);
      try {
        const uSnap = await getDoc(doc(db, "internshipusers", u.uid));
        if (uSnap.exists()) setUserData(uSnap.data());
      } catch (e) { console.error(e); }
    });
    return () => unsub();
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    const fetchHistory = async () => {
      try {
        const q = query(collection(db, "internshippaymentorder"), where("userId", "==", user.uid));
        const snap = await getDocs(q);
        const records = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        records.sort((a, b) => {
          const tA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
          const tB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
          return tB - tA;
        });
        setHistory(records);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchHistory();
  }, [user]);

  const handleDownload = (record) => {
    generateReceipt(record, user, userData);
  };

  const formatDate = (ts) => {
    if (!ts) return "—";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg,#F5F3FF,#EDE9FE,#DDD6FE)" }}>
      <div style={{ width: 52, height: 52, borderRadius: "50%", border: "4px solid #EDE9FE", borderTopColor: "#7C3AED", animation: "spin 0.8s linear infinite" }} />
    </div>
  );

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "linear-gradient(135deg,#F5F3FF 0%,#EDE9FE 50%,#F5F3FF 100%)", fontFamily: "Inter,sans-serif" }}>
      <style>{`
        @keyframes fadeInUp{from{opacity:0;transform:translateY(20px);}to{opacity:1;transform:translateY(0);}}
        @keyframes spin{to{transform:rotate(360deg);}}
        ::-webkit-scrollbar{width:6px;} ::-webkit-scrollbar-thumb{background:rgba(139,92,246,0.3);border-radius:3px;}
        .ph-main { padding: 32px 36px 48px 28px; }
        .ph-table-wrap { display: block; }
        .ph-cards-wrap { display: none; }
        @media (max-width: 768px) {
          .ph-main { padding: 72px 16px 32px 16px !important; }
          .ph-table-wrap { display: none !important; }
          .ph-cards-wrap { display: flex !important; flex-direction: column; gap: 14px; }
        }
        @media (max-width: 480px) {
          .ph-main { padding: 68px 12px 28px 12px !important; }
        }
      `}</style>

      <Sidebar user={user} userData={userData} collapsed={collapsed} setCollapsed={setCollapsed} />

      <main className="ph-main" style={{ flex: 1, overflowY: "auto", minWidth: 0 }}>
        
        <div style={{ marginBottom: 34, animation: "fadeInUp 0.4s ease both" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 6 }}>
            <div style={{ width: 52, height: 52, borderRadius: 16, background: "linear-gradient(135deg,#7C3AED,#A78BFA)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 20px rgba(124,58,237,0.32)" }}>
              <span style={{ fontSize: 24 }}>🧾</span>
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 28, fontFamily: "Outfit,sans-serif", fontWeight: 800, color: "#4C1D95" }}>Payment History</h1>
              <p style={{ margin: 0, fontSize: 14, color: "#8B5CF6", fontWeight: 500, marginTop: 4 }}>View all your transactions and download invoices.</p>
            </div>
          </div>
        </div>

        {history.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", background: "rgba(255,255,255,0.8)", borderRadius: 24, border: "1.5px solid rgba(196,181,253,0.4)" }}>
            <div style={{ fontSize: 60, marginBottom: 16 }}>📭</div>
            <h2 style={{ margin: 0, fontSize: 20, color: "#4C1D95" }}>No Transactions Found</h2>
            <p style={{ color: "#8B5CF6", marginTop: 8 }}>You haven't made any payments yet.</p>
          </div>
        ) : (
          <div style={{ animation: "fadeInUp 0.5s 0.2s ease both" }}>
            {/* Desktop Table */}
            <div className="ph-table-wrap" style={{ background: "rgba(255,255,255,0.9)", borderRadius: 24, border: "1.5px solid rgba(196,181,253,0.4)", overflow: "hidden", boxShadow: "0 8px 30px rgba(109,40,217,0.08)" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
                  <thead>
                    <tr style={{ background: "rgba(237,233,254,0.6)", borderBottom: "1.5px solid rgba(196,181,253,0.4)" }}>
                      <th style={{ padding: "16px 24px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#7C3AED", textTransform: "uppercase", letterSpacing: "0.05em" }}>Date</th>
                      <th style={{ padding: "16px 24px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#7C3AED", textTransform: "uppercase", letterSpacing: "0.05em" }}>Order ID</th>
                      <th style={{ padding: "16px 24px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#7C3AED", textTransform: "uppercase", letterSpacing: "0.05em" }}>Details</th>
                      <th style={{ padding: "16px 24px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#7C3AED", textTransform: "uppercase", letterSpacing: "0.05em" }}>Amount</th>
                      <th style={{ padding: "16px 24px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#7C3AED", textTransform: "uppercase", letterSpacing: "0.05em" }}>Status</th>
                      <th style={{ padding: "16px 24px", textAlign: "right", fontSize: 12, fontWeight: 700, color: "#7C3AED", textTransform: "uppercase", letterSpacing: "0.05em" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((record, idx) => {
                      const statusStyle = STATUS_COLORS[record.status] || STATUS_COLORS["Initiated"];
                      return (
                        <tr key={record.id} style={{ borderBottom: "1px solid rgba(196,181,253,0.2)", transition: "background 0.2s" }} onMouseEnter={e => e.currentTarget.style.background="rgba(245,243,255,0.5)"} onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                          <td style={{ padding: "16px 24px", fontSize: 13, color: "#6B7280", fontWeight: 500 }}>
                            {formatDate(record.createdAt)}
                          </td>
                          <td style={{ padding: "16px 24px" }}>
                            <div style={{ fontSize: 13, color: "#4C1D95", fontWeight: 600, fontFamily: "monospace" }}>{record.orderId}</div>
                            {record.paymentId && <div style={{ fontSize: 11, color: "#A78BFA", marginTop: 2, fontFamily: "monospace" }}>{record.paymentId}</div>}
                          </td>
                          <td style={{ padding: "16px 24px" }}>
                            <div style={{ fontSize: 13, color: "#5B21B6", fontWeight: 600 }}>{record.phase}</div>
                            <div style={{ fontSize: 12, color: "#8B5CF6", marginTop: 2 }}>{record.installmentInfo}</div>
                          </td>
                          <td style={{ padding: "16px 24px", cursor: "pointer" }} onClick={() => setExpandedId(expandedId === record.id ? null : record.id)}>
                            <div style={{ fontSize: 14, color: "#4C1D95", fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                              ₹{record.amount}
                              <span style={{ fontSize: 10, color: "#8B5CF6", background: "#EDE9FE", padding: "2px 6px", borderRadius: 4 }}>ℹ️ Split</span>
                            </div>
                            {expandedId === record.id && record.baseAmount && (
                              <div style={{ marginTop: 8, background: "rgba(245,243,255,0.8)", padding: "8px 12px", borderRadius: 8, fontSize: 11, color: "#6B7280", border: "1px dashed rgba(196,181,253,0.5)", animation: "fadeInUp 0.2s ease" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                  <span>Base:</span> <span style={{ fontWeight: 600 }}>₹{record.baseAmount}</span>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between" }}>
                                  <span>Tax:</span> <span style={{ fontWeight: 600 }}>₹{record.taxAmount}</span>
                                </div>
                              </div>
                            )}
                          </td>
                          <td style={{ padding: "16px 24px" }}>
                            <span style={{ background: statusStyle.bg, color: statusStyle.text, padding: "4px 10px", borderRadius: 8, fontSize: 11, fontWeight: 700 }}>
                              {record.status}
                            </span>
                          </td>
                          <td style={{ padding: "16px 24px", textAlign: "right" }}>
                            {record.status === "Success" ? (
                              <button onClick={() => handleDownload(record)} style={{ padding: "8px 16px", borderRadius: 10, border: "1.5px solid rgba(167,139,250,0.6)", background: "transparent", color: "#7C3AED", fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.2s" }} onMouseEnter={e => e.currentTarget.style.background="rgba(237,233,254,0.8)"} onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                                Download Invoice
                              </button>
                            ) : (
                               <span style={{ fontSize: 12, color: "#9CA3AF" }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Cards */}
            <div className="ph-cards-wrap">
              {history.map((record, idx) => {
                const statusStyle = STATUS_COLORS[record.status] || STATUS_COLORS["Initiated"];
                return (
                  <div key={record.id} style={{ background: "rgba(255,255,255,0.92)", borderRadius: 20, border: "1.5px solid rgba(196,181,253,0.4)", padding: "18px 20px", boxShadow: "0 4px 20px rgba(109,40,217,0.08)", animation: `fadeInUp 0.4s ${idx * 0.06}s ease both` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <span style={{ background: statusStyle.bg, color: statusStyle.text, padding: "4px 10px", borderRadius: 8, fontSize: 11, fontWeight: 700 }}>{record.status}</span>
                      <span style={{ fontSize: 12, color: "#9CA3AF" }}>{formatDate(record.createdAt)}</span>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 13, color: "#5B21B6", fontWeight: 700 }}>{record.phase}</div>
                      <div style={{ fontSize: 12, color: "#8B5CF6", marginTop: 2 }}>{record.installmentInfo}</div>
                    </div>
                    <div style={{ fontFamily: "monospace", fontSize: 11, color: "#A78BFA", marginBottom: 12, wordBreak: "break-all" }}>{record.orderId}</div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 12, borderTop: "1px solid rgba(196,181,253,0.25)" }}>
                      <div>
                        <div style={{ fontSize: 18, color: "#4C1D95", fontWeight: 800 }}>₹{record.amount}</div>
                        {record.baseAmount && (
                          <div style={{ fontSize: 10, color: "#8B5CF6", marginTop: 2 }}>Base: ₹{record.baseAmount} + Tax: ₹{record.taxAmount?.toFixed(2)}</div>
                        )}
                      </div>
                      {record.status === "Success" ? (
                        <button onClick={() => handleDownload(record)} style={{ padding: "9px 16px", borderRadius: 10, border: "1.5px solid rgba(167,139,250,0.6)", background: "rgba(237,233,254,0.6)", color: "#7C3AED", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                          📄 Invoice
                        </button>
                      ) : (
                        <span style={{ fontSize: 12, color: "#9CA3AF" }}>No invoice</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PaymentHistoryScreen;
